import {
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  UncontrolledTooltip,
  Spinner
  // @ts-ignore
} from 'reactstrap'
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ChatFile } from './ChatFile'
import { DragDropArea } from './DragDropArea'
import { sizeInBytes } from '../../../configs/constants'
import { useFeatureGate } from '../../../_hooks/useFeatureGate'
import { toast } from 'react-toastify'
import { PromptLibraryIcon, StopCircle, WaveSound } from './icons/Icons'
import { useDispatch, useSelector } from 'react-redux'
import { Tooltip } from 'react-tooltip'
import {
  attachOptions,
  convertFileToBase64,
  fileSizeError,
  imageCountLimit,
  imageSizeLimit,
  imgChatError,
  imgLimitError,
  imgSizeError,
  maxImageLimit,
  maxImgError,
  uploadErrorMessage,
  validFileInput,
  validImageInput,
  welcomePageSuggestions,
  MCP_SERVER_NAMES,
  fileCountLimit,
  fileSizeLimit
} from './utils/constants'
import _endpoints from '../../../_endpoints'
import {
  checkIsDrawerMode,
  getFileDisplayName,
  resolveWebSearchToolConfig,
  resolveSnowflakeToolConfig,
  resolveSmartInsightsToolConfig,
  buildDataProductScopeFields
} from './utils/helpers'
import { useDataboxService } from '../Teams/services/databoxService'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { appInsights } from '../../../_services/AppInsights'
import { EventType } from '../../../utility/types'
import { ChatbotContext } from './context/ChatbotProvider'
import { useChatService } from './services/chatService'
import useSpeech from '../../../_hooks/useSpeech'
import { ChatImage } from './ChatImage'
import { getFileType, hasImages, isTempMessageId, isAbortedContent } from './utils/TextUtils'
import { FileProps, WebSearchToolConfig } from './utils/types'
//@ts-ignore
import { Button } from 'dsm-react-lib'
import PromptLibraryModal from './modals/PromptLibraryModal'
import { setSelectedChatModel } from '../../../_redux/actions/chatbotActions'
import ChatModelDropdown from './chat-history/ChatModelDropdown'
import { DataSourceSelect } from './DataSourceSelect'
import { setProducts } from '../../../_redux/actions/dataMarketPlaceActions'
import {
  buildPersonalDataBoxesPayload,
  extractOriginalDataSourceIds,
  findFirstMcpTeamIdentifier,
  findMcpScopeConfiguration,
  buildSmartInsightsScopeConfiguration,
  parseMcpServerSources
} from './utils/mcpServerUtils'
import {
  IconWorld,
  IconX,
  IconMicrophone,
  IconMicrophoneOff,
  IconSend,
  IconSquarePlus,
  IconSquare,
  IconCircleChevronsUp,
  IconCircleChevronsDown
} from '@tabler/icons-react'

// Collapsed textarea grows up to this many lines before it scrolls internally
const MAX_COLLAPSED_LINES = 8

interface ChatInputProps {
  documentFileRef: any
  inputFileRef: any
  promptText: string
  setPromptText: any
  isLoadingAgents: boolean
  parentContainerWidth?: number | null
  // True when the composer is used inside a project (project landing page composer).
  // Project chats hide the connect databox / data product attach options.
  isProjectChat?: boolean
  // True when the Ali context bar is shown directly above; flattens the top edge
  // so the bar and composer read as one continuous frame (Spec 010 §8.2).
  hasContextBar?: boolean
  onNewConversationStart: () => void
  // Controlled expand state, set by ChatRoom so its layout updates in the same render.
  isExpanded?: boolean
  // Notifies the parent (ChatRoom) when expand is toggled, so it can resize its layout.
  onExpandedChange?: (expanded: boolean) => void
}

export const ChatInput: React.FC<ChatInputProps> = ({
  documentFileRef,
  inputFileRef,
  promptText,
  setPromptText,
  parentContainerWidth,
  isProjectChat = false,
  isLoadingAgents,
  hasContextBar = false,
  onNewConversationStart,
  isExpanded: controlledIsExpanded,
  onExpandedChange
}) => {
  const [value, setValue] = useState('')
  const [fileList, setFileList] = useState<FileProps[]>([])
  const [imgList, setImgList] = useState<any>([])
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const [inputFocused, setInputFocused] = useState(false)
  const [isSearchHovered, setIsSearchHovered] = useState(false)
  const dataSourceRef = useRef<any>(null)
  const [dataSourceState, setDataSourceState] = useState<any>({
    selectedDataboxes: [],
    selectedDataProducts: [],
    combinedDataboxIds: [],
    combinedDataProductIds: []
  })
  const fileListRef = useRef<any>(undefined)
  const [fileListHeight, setFileListHeight] = useState<number | undefined>(undefined)
  const { value: centrali3_0 } = useFeatureGate('centrali3_0', false)
  const { chatModels, selectedChatModel, mcpServers, selectedAgent, isFullScreen } = useSelector(
    (state: any) => state.chatbotReducer
  )
  const dispatch = useDispatch()
  const textarea = document.getElementById('chat-input')
  //@ts-ignore
  const trackMicrophoneUsed = useTrackEvent(appInsights, EventType.MicrophoneUsed)
  //@ts-ignore
  const trackModelVersionUsed = useTrackEvent(appInsights, EventType.ModelVersionUsed)
  // @ts-ignore
  const trackResourceUsed = useTrackEvent(appInsights, EventType.ResourceUsed)
  const [time, setTime] = useState({ minutes: 0, seconds: 0 })
  const formattedMinutes = time.minutes < 10 ? `0${time.minutes}` : time.minutes
  const formattedSeconds = time.seconds < 10 ? `0${time.seconds}` : time.seconds
  const [showWaveSound, setShowWaveSound] = useState(false)
  const [openPromptLib, setOpenPromptLib] = useState(false)
  const [shuffledPromptSuggestions] = useState(() =>
    [...welcomePageSuggestions].sort(() => 0.5 - Math.random())
  )
  // Fullscreen composer view, for typing long prompts with room to see what you're writing.
  // Falls back to internal state when no parent controls it (e.g. ProjectView's composer).
  const [internalIsExpanded, setInternalIsExpanded] = useState(false)
  const isExpanded = controlledIsExpanded ?? internalIsExpanded
  const setIsExpanded = (next: boolean | ((prev: boolean) => boolean)) => {
    const resolved =
      typeof next === 'function' ? (next as (prev: boolean) => boolean)(isExpanded) : next
    setInternalIsExpanded(resolved)
    onExpandedChange?.(resolved)
  }
  // Only show the expand toggle once the textarea actually has more content than it can show
  const [hasOverflow, setHasOverflow] = useState(false)
  const waveSoundTimerRef = useRef<any>(null)
  const [isDrawerMode] = useState(checkIsDrawerMode())
  const [hasMicrophoneAccess, setHasMicrophoneAccess] = useState(false)
  const {
    chatMode,
    setDisableInput,
    disableInput,
    messages = [],
    rawMessages = [],
    isStreaming,
    attachments = { count: 0, previewFile: null },
    dataProductsInChat,
    isWebSearchEnabled,
    setIsWebSearchEnabled,
    currentChat,
    fetchingMsgs,
    threadMcpServers,
    setThreadMcpServers,
    threadPersonalDataBoxes,
    projectThreadIdsRef
  } = useContext(ChatbotContext)
  const { handleChatSubmit, cancelAPICall, uploadChatImages } = useChatService()
  const { getDataboxDataProductsPaginated } = useDataboxService()
  // Tracks whether the component is currently resolving data sources (e.g., fetching or parsing them) to enable/disable conditional submit button behavior
  const [isResolvingDataSources, setIsResolvingDataSources] = useState(true)

  // True when this chat belongs to a project (project composer, or a project-owned
  // thread). Project chats hide the connect databox / data product attach options.
  /* eslint-disable react-hooks/refs */
  const isInProjectChat =
    isProjectChat || (currentChat?.id ? projectThreadIdsRef.current.has(currentChat.id) : false)
  /* eslint-enable react-hooks/refs */

  const showSubmit =
    (value.trim().length > 0 || fileList?.length > 0 || imgList?.length > 0) &&
    !isStreaming &&
    !selectedAgent?.isDeleted
  const {
    transcript,
    isListening,
    resetTranscript,
    browserSupportsSpeech,
    isMicrophoneAvailable,
    checkSpeechAccess,
    startListening,
    stopListening
  } = useSpeech()

  const isAgentDeleted = selectedAgent?.isDeleted === true

  // Agent's default databox/data product IDs, parsed from agentMcpServers
  const {
    databoxIds: agentDataboxIds,
    dataProductIds: agentDataProductIds,
    teamIdentifiers: agentTeamIdentifiers,
    cognitiveSearchIds: agentCognitiveSearchIds,
    dataProductsByDatabox: agentDataProductsByDatabox,
    smartInsightsTeamIdentifier: agentSmartInsightsTeamIdentifier,
    webSearchId: agentWebSearchId
  } = React.useMemo(
    () => parseMcpServerSources(selectedAgent?.agentMcpServers, mcpServers),
    // agentMcpServers too: an edited+saved agent keeps its id but gets a new config object
    [selectedAgent?.id, selectedAgent?.agentMcpServers, mcpServers]
  )

  // This thread's own databox/data product IDs, parsed the same way from threadMcpServers -
  // set atomically by ChatHistory.tsx's getDataSourcesForChat
  const {
    databoxIds: threadSourceDataboxIds,
    dataProductIds: threadSourceDataProductIds,
    cognitiveSearchIds: threadSourceCognitiveSearchIds,
    dataProductsByDatabox: threadSourceDataProductsByDatabox,
    webSearchId: threadSourceWebSearchId
  } = React.useMemo(
    () => parseMcpServerSources(threadMcpServers, mcpServers),
    [threadMcpServers, mcpServers]
  )

  // This thread's Personal Databox scope - read from the separate top-level personalDataBoxes
  // field (a sibling to threadMcpServers, not nested inside it).
  const threadPersonalDataBoxEntry = threadPersonalDataBoxes?.[0]
  const threadSourcePersonalDataboxResourceIds: string[] =
    threadPersonalDataBoxEntry?.resourceIds || []
  const threadSourcePersonalDataBoxId: string | undefined =
    threadPersonalDataBoxEntry?.personalDataBoxId
  const isAgentWebSearchLocked = Boolean(agentWebSearchId)
  const effectiveWebSearchEnabled = isAgentWebSearchLocked
    ? true
    : isWebSearchEnabled ??
      (threadSourceWebSearchId === null || threadSourceWebSearchId === undefined
        ? Boolean(agentWebSearchId?.length)
        : threadSourceWebSearchId !== '')

  // The agent's own Personal Databox default - also a separate top-level field on the agent
  // Falls back into DataSourceSelect only until the thread has its own confirmed selection.
  const agentPersonalDataBoxEntry = selectedAgent?.personalDataBoxes?.[0]
  const agentSourcePersonalDataboxResourceIds: string[] =
    agentPersonalDataBoxEntry?.resourceIds || []
  const agentSourcePersonalDataBoxId: string | undefined =
    agentPersonalDataBoxEntry?.personalDataBoxId

  const isMarketplace =
    location.pathname.includes('data-marketplace') ||
    location.pathname.includes('data-product-admin')
  const products = useSelector((state: any) => state?.dataMarketPlaceReducer?.products)
  // imported from TextUtils: isTempMessageId, isAbortedContent

  const getStableResponseId = (response: any): string | null => {
    const responseId = response?.id
    if (!responseId || isTempMessageId(responseId)) return null
    if (isAbortedContent(response?.content)) return null
    return responseId
  }
  const intervalRef = useRef<any>(null)

  // Agent's web search default changed (e.g. agent edited and saved) - on a not-yet-started chat,
  // drop the stale value so the effect below re-derives it from the updated agent config.
  const previousAgentWebSearchIdRef = useRef(agentWebSearchId)
  useEffect(() => {
    if (previousAgentWebSearchIdRef.current === agentWebSearchId) return
    previousAgentWebSearchIdRef.current = agentWebSearchId
    if (!currentChat?.id && !messages.length) {
      setIsWebSearchEnabled(null)
    }
  }, [agentWebSearchId])

  useEffect(() => {
    if (!mcpServers?.length) return

    if (isAgentWebSearchLocked) {
      if (isWebSearchEnabled !== true) {
        setIsWebSearchEnabled(true)
      }
      return
    }

    const isWebSearchON =
      threadSourceWebSearchId === null || threadSourceWebSearchId === undefined
        ? Boolean(agentWebSearchId?.length)
        : threadSourceWebSearchId !== ''

    // Apply defaults only before initialization; preserve manual choices once unlocked.
    if (isWebSearchEnabled === null) {
      setIsWebSearchEnabled(isWebSearchON)
    }
  }, [
    agentWebSearchId,
    isAgentWebSearchLocked,
    isWebSearchEnabled,
    mcpServers?.length,
    threadSourceWebSearchId
  ])

  // useLayoutEffect: clears/sets the inline height synchronously before paint,
  // so switching to/from flex-driven expanded sizing never flashes the stale inline pixel height.
  useLayoutEffect(() => {
    if (textarea && inputFileRef.current) {
      if (isExpanded) {
        // Flex layout sizes the textarea while expanded, so don't force an inline height
        inputFileRef.current.style.height = ''
        setHasOverflow(inputFileRef.current.scrollHeight > inputFileRef.current.clientHeight)
        return
      }
      // Always reset first so shrinking (including clearing to empty) is picked up, not just growth
      inputFileRef.current.style.height = 'auto'
      if (value !== '') {
        // Cap collapsed growth to 8 lines, then let the textarea's own scrollbar take over
        const lineHeight = parseFloat(getComputedStyle(inputFileRef.current).lineHeight) || 20
        const maxCollapsedHeight = lineHeight * MAX_COLLAPSED_LINES
        const cappedHeight = Math.min(inputFileRef.current.scrollHeight, maxCollapsedHeight)
        inputFileRef.current.style.height = `${cappedHeight}px`
        setHasOverflow(inputFileRef.current.scrollHeight > cappedHeight)
      } else {
        setHasOverflow(false)
      }
    }
  }, [value, inputFocused, isExpanded])

  useEffect(() => {
    if (!messages.length) {
      setValue('')
      setPromptText('')
      setFileList([])
      setImgList([])
    }
  }, [messages, setPromptText])

  useEffect(() => {
    setValue(promptText)
    setTimeout(() => {
      inputFileRef.current?.focus()
    }, 5)
  }, [promptText])

  useEffect(() => {
    if (isListening) {
      intervalRef.current = setInterval(() => {
        setTime((prevTime) => {
          const { minutes, seconds } = prevTime
          if (seconds === 59) {
            return { minutes: minutes + 1, seconds: 0 }
          } else {
            return { minutes, seconds: seconds + 1 }
          }
        })
      }, 1000)
    } else {
      if (time.minutes > 0 || time.seconds > 0) {
        //track microphone used event
        trackMicrophoneUsed({
          timeInSeconds: time.minutes * 60 + time.seconds // eslint-disable-line no-mixed-operators
        })
      }
      setTime({ minutes: 0, seconds: 0 })
      clearInterval(intervalRef.current)
    }
    return () => clearInterval(intervalRef.current)
  }, [isListening])

  useEffect(() => {
    if (time.minutes === 2) {
      stopListening()
      setTime({ minutes: 0, seconds: 0 })
      clearInterval(intervalRef.current)
    }
  }, [time])

  useEffect(() => {
    setDisableInput(fileList?.some((file: FileProps) => file.state !== 'attached'))
  }, [fileList])

  useEffect(() => {
    if (transcript !== '') {
      setValue(transcript)
      if (waveSoundTimerRef.current) clearTimeout(waveSoundTimerRef.current)
      setShowWaveSound(true)
      waveSoundTimerRef.current = setTimeout(() => {
        setShowWaveSound(false)
      }, 2000)
    }
  }, [transcript])

  useEffect(() => {
    setHasMicrophoneAccess(isMicrophoneAvailable)
  }, [isMicrophoneAvailable])

  useEffect(() => {
    // Reset hover state when web search is toggled
    setIsSearchHovered(false)
  }, [isWebSearchEnabled])

  useEffect(() => {
    if (!isExpanded) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsExpanded(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isExpanded])

  const getStableIdFromEntry = (entry: any): string | null => {
    if (!entry) return null

    // direct message shape (rawMessages)
    if (entry?.id && !isTempMessageId(entry.id) && !isAbortedContent(entry?.content)) {
      return entry.id
    }

    // chat message shape (response array)
    const responses = Array.isArray(entry?.response) ? entry.response : []
    if (!responses.length) return null

    const activeIndex =
      typeof entry?.activeResponseIndex === 'number'
        ? entry.activeResponseIndex
        : responses.length - 1

    const activeId = getStableResponseId(responses?.[activeIndex])
    if (activeId) return activeId

    // fallback: latest non-temp id
    for (let i = responses.length - 1; i >= 0; i--) {
      const responseId = getStableResponseId(responses[i])
      if (responseId) return responseId
    }

    return null
  }

  const getStableParentMessageId = (): string | null => {
    // Prefer raw message history because it preserves exact chronological order.
    for (let i = (rawMessages?.length || 0) - 1; i >= 0; i--) {
      const id = getStableIdFromEntry(rawMessages[i])
      if (id) return id
    }

    // Fallback to formatted messages.
    for (let i = (messages?.length || 0) - 1; i >= 0; i--) {
      const id = getStableIdFromEntry(messages[i])
      if (id) return id
    }

    return null
  }

  const onDragLeave = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    if (chatMode === 'smartData') return
    if (e.type === 'drop' || e.type === 'dragleave') {
      //Get dropzone div box modal
      const dropZoneDiv = document.getElementById('chat-dropzone-container')
      const dropZoneDivBoxModal = dropZoneDiv ? dropZoneDiv.getBoundingClientRect() : null
      const xDistLeft = e.clientX - (dropZoneDivBoxModal ? dropZoneDivBoxModal.left : 0) //Calc cursor distance from box left
      const yDistTop = e.clientY - (dropZoneDivBoxModal ? dropZoneDivBoxModal.top : 0) //Calc cursor distance from box top
      const xDistRight = dropZoneDivBoxModal ? dropZoneDivBoxModal.right : 0 //Calc cursor distance from box right
      const yDistBottom = dropZoneDivBoxModal ? dropZoneDivBoxModal.bottom : 0 //Calc cursor distance from box bottom

      //Check if mouse cursor falls outside of the box
      if (
        xDistLeft <= 0 ||
        yDistTop <= 0 ||
        e.clientX > xDistRight - 1 ||
        e.clientY > yDistBottom - 1
      ) {
        setIsDraggingOver(false)
      }
    }
  }

  // Resolve the full set of data product IDs Snowflake should see: standalone picks, products
  // nested in a databox, and (for a fully-selected databox) all of its data products, fetched
  // the same way agent submission does.
  const resolveSnowflakeDataProductIds = async (standaloneIds: string[]): Promise<string[]> => {
    const idsSet = new Set<string>(standaloneIds.map(String))
    const databoxes = dataSourceState.selectedDataboxes || []

    const fullySelectedDataboxIds: string[] = []
    databoxes.forEach((db: any) => {
      const nestedProductIds = (db.selectedItems || [])
        .filter((item: any) => item.type === 'dataproduct')
        // Same real-id preference as the agent-authoring side
        .map((item: any) => String(item.dataProductId ?? item.id))
      nestedProductIds.forEach((id: string) => idsSet.add(id))

      // Whole databox selected (no selectedItems) - all its data products are in scope.
      // This Nexus API needs the databox's internal `id`, not `smartDocsDataboxId`.
      if (!db.selectedItems || db.selectedItems.length === 0) {
        fullySelectedDataboxIds.push(String(db.id || db.smartDocsDataboxId))
      }
    })

    if (fullySelectedDataboxIds.length > 0) {
      const results = await Promise.all(
        fullySelectedDataboxIds.map((databoxId) =>
          getDataboxDataProductsPaginated(databoxId, 1, 50).catch((error: any) => {
            console.error(`Error fetching data products for databox ${databoxId}:`, error)
            return []
          })
        )
      )
      results.forEach((products: any) => {
        ;(Array.isArray(products) ? products : []).forEach((product: any) => {
          const id = product?.dataProductExternalId || product?.id
          if (id) idsSet.add(String(id))
        })
      })
    }

    return Array.from(idsSet)
  }

  const handleSubmit = async (value: string, files: FileProps[], images: any[]) => {
    // Validate before clearing inputs
    if (disableInput || (value.trim().length === 0 && (!images || images.length === 0))) {
      return
    }

    // Clear inputs only after validation passes
    setValue('')
    setPromptText('')

    try {
      // Handle image upload if needed
      let uploadedImages: any[] = images || []
      if (images && images.length > 0 && typeof images[0].content === 'string') {
        const results = await uploadChatImages(images)
        if (results?.error) {
          toast.error(results.error || 'Image upload failed')
          return
        }
        uploadedImages = (results.images || []).map((r: any) => ({
          id: r.imageId,
          name: r.imageId.split('_')[1],
          content: r.url
        }))
      }

      // Prepare submission data
      const parentId = getStableParentMessageId()
      const fileList: FileProps[] = files.map((file) => ({
        id: file.id,
        name: getFileDisplayName(file.name),
        type: 'file'
      }))

      // Track usage
      trackModelVersionUsed({
        chatType: 'default',
        modelVersion: selectedChatModel?.name
      })
      if (fileList && fileList.length > 0) {
        trackResourceUsed({
          fileType: fileList.map((file: FileProps) => getFileType(file.name)).join(', ') || ''
        })
      }

      // ---------------------WEB SEARCH, SNOWFLAKE & AGENT MCP SERVERS-------------------------- //
      const isFirstRequest = !parentId

      // Initialize MCP server map and helper function

      const threadMcpServersMap = new Map<string, any>()
      const addThreadMcpServer = (server: any) => {
        const mcpServerId = server?.mcpServerId || server?.id
        if (!mcpServerId) return

        const normalizedServer =
          server?.mcpServerId !== undefined
            ? server
            : {
                mcpServerId,
                isEnabled: true
              }

        threadMcpServersMap.set(mcpServerId, normalizedServer)
      }

      // Get Web Search MCP server
      const webSearchMcpServer = mcpServers?.find(
        (server: any) => server?.name === MCP_SERVER_NAMES.WEB_SEARCH
      )

      // Get Snowflake (Text-to-SQL) MCP server
      const snowflakeServer = mcpServers?.find(
        (server: any) => server?.name === MCP_SERVER_NAMES.SNOWFLAKE
      )

      // Get SmartInsights MCP server
      const smartInsightsServer = mcpServers?.find(
        (server: any) => server?.name === MCP_SERVER_NAMES.SMART_INSIGHTS
      )

      // Get Code Interpreter MCP server
      const codeInterpreterServer = mcpServers?.find(
        (server: any) => server?.name === MCP_SERVER_NAMES.CODE_INTERPRETER
      )

      // Include WebSearch MCP server on every request. threadMcpServers is sent as the full
      // authoritative list each time (not merged server-side), so if a later message adds
      // databox/data product MCP servers (shouldIncludeAgentMcpServers) without re-adding it,
      // it would be silently dropped from the thread.
      if (webSearchMcpServer && isWebSearchEnabled) {
        addThreadMcpServer(webSearchMcpServer)
      }

      // CodeInterpreter is persisted on the agent (via otherMCPs). Fall back to adding it at
      // thread level only for legacy agents that don't have it saved yet.
      const agentHasCodeInterpreter = Boolean(
        codeInterpreterServer &&
          selectedAgent?.agentMcpServers?.some(
            (s: any) => s?.mcpServerId === codeInterpreterServer.id
          )
      )
      if (codeInterpreterServer && !agentHasCodeInterpreter) {
        addThreadMcpServer(codeInterpreterServer)
      }

      const getWebSearchToolConfig = (): WebSearchToolConfig | undefined =>
        resolveWebSearchToolConfig(webSearchMcpServer)

      const getSnowflakeToolConfig = () => resolveSnowflakeToolConfig(snowflakeServer)

      const getSmartInsightsToolConfig = () => resolveSmartInsightsToolConfig(smartInsightsServer)

      // Personal Databox files (if any are selected) go out as their own top-level payload
      // field (personalDataBoxes) - a sibling to threadMcpServers, NOT nested inside it.
      const personalDataBoxes = buildPersonalDataBoxesPayload(
        dataSourceState.selectedDataboxes || []
      )

      // Resolve once so both Snowflake blocks below use the same expanded set. Skipped
      // (synchronous passthrough) when no databoxes are selected, or this agent has no
      // Snowflake MCP server at all - nothing to expand/send it to in that case.
      const resolvedSnowflakeDataProductIds =
        (dataSourceState.selectedDataboxes || []).length > 0 && snowflakeServer
          ? await resolveSnowflakeDataProductIds(dataSourceState.combinedDataProductIds)
          : dataSourceState.combinedDataProductIds

      // Determine if we should include agent MCP servers
      // On first request, always include. On subsequent requests, include if data sources are available
      const shouldIncludeAgentMcpServers =
        isFirstRequest ||
        dataSourceState.combinedDataboxIds.length > 0 ||
        dataSourceState.combinedDataProductIds.length > 0

      // Add agent's default MCP servers so they're sent even before the user picks any data.
      if (
        shouldIncludeAgentMcpServers &&
        selectedAgent?.agentMcpServers &&
        Array.isArray(selectedAgent.agentMcpServers)
      ) {
        selectedAgent.agentMcpServers.forEach((mcpServer: any) => {
          // Externally-sourced Snowflake agents have no scopeConfiguration (scope lives outside Centrali) - still send the server id/enabled flag
          if (
            mcpServer.mcpServerId &&
            !mcpServer.scopeConfiguration &&
            selectedAgent?.externalType === 'Snowflake'
          ) {
            addThreadMcpServer({
              mcpServerId: mcpServer.mcpServerId,
              isEnabled: mcpServer.isEnabled !== false
            })
          } else if (mcpServer.mcpServerId && mcpServer.scopeConfiguration) {
            try {
              // Parse scopeConfiguration (handles both string and pre-parsed object)
              const originalScopeConfig =
                typeof mcpServer.scopeConfiguration === 'string'
                  ? JSON.parse(mcpServer.scopeConfiguration)
                  : mcpServer.scopeConfiguration

              if (
                mcpServer.type === 'PersonalDatabox' ||
                originalScopeConfig?.type === 'PersonalDatabox'
              ) {
                return
              }

              // Check if this is SmartInsights server
              const isSmartInsights = mcpServer.mcpServerId === smartInsightsServer?.id
              const hasDataboxes = dataSourceState.combinedDataboxIds.length > 0
              const hasDataProducts = dataSourceState.combinedDataProductIds.length > 0

              // Skip SmartInsights if there are no databoxes, or if Personal Databox files are
              // selected - Personal Databox scope is sent as its own top-level field instead.
              if (isSmartInsights && !hasDataboxes) {
                return
              }

              const {
                dataBoxIds: _dataBoxIds,
                dataProductIds: _dataProductIds,
                filters: _filters,
                customSelection: _customSelection,
                ...otherConfig
              } = originalScopeConfig
              const modifiedScopeConfig: any = isSmartInsights
                ? buildSmartInsightsScopeConfiguration({
                    originalScopeConfiguration: originalScopeConfig,
                    currentDataboxIds: dataSourceState.combinedDataboxIds,
                    selectedDataboxes: dataSourceState.selectedDataboxes,
                    trackingId: currentChat?.id || null,
                    preserveOtherConfig: true
                  })
                : { ...otherConfig }

              // SmartInsights: reuse the original filter if the databox selection is unchanged,
              // otherwise rebuild it. trackingId links this scope to the current thread.
              if (isSmartInsights) {
                // The shared helper keeps the original filter when the databox selection is unchanged.
              } else if (hasDataProducts || hasDataboxes) {
                // Structured Data (Snowflake) and other non-SmartInsights servers only care
                // about data products - databox scope is not part of their payload. Databox-
                // nested groups from the original config are preserved untouched. This also
                // includes data products belonging to a fully-selected databox, resolved above.
                Object.assign(
                  modifiedScopeConfig,
                  buildDataProductScopeFields(originalScopeConfig, resolvedSnowflakeDataProductIds)
                )
              }

              // Add to thread MCP servers
              addThreadMcpServer({
                mcpServerId: mcpServer.mcpServerId,
                isEnabled: mcpServer.isEnabled !== false, // Default to true if not specified
                scopeConfiguration: modifiedScopeConfig
              })
            } catch (error) {
              console.error('Error parsing scopeConfiguration for MCP server:', error)
            }
          }
        })
      }

      // Update lastSent state after building MCP servers so next message can detect changes
      // Note: This is now handled internally by DataSourceSelect

      // Use combinedDataboxIds/combinedDataProductIds from dataSourceState. When starting a new
      // chat from the data marketplace, also merge in the products picked there so they flow
      // through this same code path instead of a separate marketplace-only one (below).
      const marketplaceDataProductIds = isMarketplace
        ? products?.map((p: any) => p.value) || []
        : []
      const dataBoxIds = dataSourceState.combinedDataboxIds
      const dataProductIds = [
        ...new Set([...resolvedSnowflakeDataProductIds, ...marketplaceDataProductIds])
      ]
      const hasDataboxes = dataBoxIds.length > 0
      const hasDataProducts = dataProductIds.length > 0

      // Extract teamIdentifier with proper priority (needed in MCP scopeConfiguration)
      // 1. First, try to get from agent's MCP server scopeConfiguration (original team context)
      let teamIdentifier: string | undefined =
        findFirstMcpTeamIdentifier(selectedAgent?.agentMcpServers) ||
        findFirstMcpTeamIdentifier(threadMcpServers)

      // 3. If still not found, fall back to modal-added databoxes
      if (!teamIdentifier) {
        teamIdentifier = dataSourceState.selectedDataboxes.find(
          (db: any) => db.teamIdentifier
        )?.teamIdentifier
      }

      // Look up a specific MCP server's original scopeConfiguration from the agent's own config.
      // Used to reuse the SmartInsights `filter` untouched when unchanged, and to preserve
      // Snowflake's databox-nested data product groups when rebuilding its submit payload.
      const originalSmartInsightsConfig = findMcpScopeConfiguration(
        selectedAgent?.agentMcpServers,
        smartInsightsServer?.id
      )
      const originalSnowflakeConfig = findMcpScopeConfiguration(
        selectedAgent?.agentMcpServers,
        snowflakeServer?.id
      )

      // Add SmartInsights MCP server when user has selected databoxes (manual selection, not from agent)
      // Reuse the original filter if unchanged, otherwise rebuild it. trackingId links this
      // scope to the current thread (null until the thread exists). Skipped entirely when
      // Personal Databox files are selected - that goes out via its own dedicated entry above.
      if (hasDataboxes && smartInsightsServer) {
        addThreadMcpServer({
          mcpServerId: smartInsightsServer.id,
          isEnabled: true,
          scopeConfiguration: buildSmartInsightsScopeConfiguration({
            originalScopeConfiguration: originalSmartInsightsConfig,
            currentDataboxIds: dataBoxIds,
            selectedDataboxes: dataSourceState.selectedDataboxes,
            teamIdentifier,
            trackingId: currentChat?.id || null,
            preserveOtherConfig: false
          })
        })
      }

      // Add Snowflake MCP server when user has selected data products (manual selection, not
      // from agent). Snowflake only cares about data products - databox scope is not part of
      // its payload. Databox-nested data product groups from the agent's original config are
      // preserved untouched; only the standalone group is replaced with the current selection.
      if (hasDataProducts && snowflakeServer) {
        addThreadMcpServer({
          mcpServerId: snowflakeServer.id,
          isEnabled: true,
          scopeConfiguration: buildDataProductScopeFields(originalSnowflakeConfig, dataProductIds)
        })
      }

      // Convert map to array for submission
      const outgoingThreadMcpServers = Array.from(threadMcpServersMap.values())

      // Use databox/dataproduct objects from dataSourceState
      const databoxObjects = dataSourceState.selectedDataboxes
      const dataProductObjects = dataSourceState.selectedDataProducts

      // Resolve tool configurations based on which MCP servers are actually in threadMcpServers
      // This ensures tool configs match what's actually being sent to backend
      const hasSnowflakeInThread = outgoingThreadMcpServers.some(
        (mcp: any) => mcp.mcpServerId === snowflakeServer?.id
      )
      const hasSmartInsightsInThread = outgoingThreadMcpServers.some(
        (mcp: any) => mcp.mcpServerId === smartInsightsServer?.id
      )

      //returning undefined for webSearchToolConfig to set toolConfiguration mode as auto
      const webSearchToolConfig = isWebSearchEnabled ? getWebSearchToolConfig() : undefined
      const snowflakeToolConfigs = hasSnowflakeInThread ? getSnowflakeToolConfig() : undefined
      const smartInsightsToolConfig = hasSmartInsightsInThread
        ? getSmartInsightsToolConfig()
        : undefined

      // Build unified mcpToolConfigs array
      // Snowflake returns an array (GetQuery + ExecuteQuery), so spread it
      const mcpToolConfigs = [
        webSearchToolConfig,
        ...(snowflakeToolConfigs || []),
        smartInsightsToolConfig
      ].filter((config): config is { mcpServerId: string; toolName: string } => !!config)

      // For submission, check if combined objects exist (for selectedDataboxes/selectedDataProducts params)
      const hasCombinedDataboxes = databoxObjects.length > 0
      const hasCombinedDataProducts = dataProductObjects.length > 0
      const finalThreadMcpServers =
        shouldIncludeAgentMcpServers || outgoingThreadMcpServers.length > 0
          ? outgoingThreadMcpServers
          : []
      setThreadMcpServers(finalThreadMcpServers)

      // Submit chat
      await handleChatSubmit({
        prompt: value.trim(),
        parentId,
        files: fileList,
        images: uploadedImages,
        //...(mcpToolConfigs.length > 0 && { mcpToolConfigs }), //commented this to set toolConfiguration mode as auto
        // Pass threadMcpServers if agent config changed OR if manual MCP servers added
        ...(finalThreadMcpServers && {
          threadMcpServers: finalThreadMcpServers
        }),
        ...(hasCombinedDataboxes && { selectedDataboxes: databoxObjects }),
        ...(hasCombinedDataProducts && { selectedDataProducts: dataProductObjects }),
        ...(personalDataBoxes && { personalDataBoxes })
      })

      // Clear file lists on success
      setFileList([])
      setImgList([])
    } catch (err) {
      console.error('Chat submission error:', err)
      // More specific error message based on context
      if (images && images.length > 0) {
        toast.error('Failed to upload images or submit chat. Please try again.')
      } else {
        toast.error('Failed to submit chat. Please try again.')
      }
    }
  }

  //validate single file
  const validateFile = async (
    file: File,
    isImgMode: boolean,
    fileSizeLimit: number,
    imageSizeLimit: number
  ) => {
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`
    const validInputs = isImgMode ? validImageInput : validFileInput
    const finalFileSizeLimit =
      fileExtension === '.xlsx' ? 0.25 : fileExtension === '.csv' ? 1 : fileSizeLimit // 250 KB for Excel files, 1 MB for CSV files
    const sizeLimit = (isImgMode ? imageSizeLimit : finalFileSizeLimit) * sizeInBytes
    const fileName = getFileDisplayName(file.name)

    const errors = {
      duplicateFile: fileList.some((f: FileProps) => getFileDisplayName(f.name) === fileName)
        ? `A file named "${fileName}" is already attached. Please select a different file.`
        : null,
      invalidFormat: !validInputs.includes(fileExtension)
        ? `Invalid ${isImgMode ? 'image' : 'file'} format! Please upload ${validInputs.join(
            ', '
          )} only.`
        : null,
      sizeExceeded:
        file.size > sizeLimit
          ? isImgMode
            ? imgSizeError
            : fileSizeError(finalFileSizeLimit)
          : null
    }

    const error = errors.duplicateFile || errors.invalidFormat || errors.sizeExceeded
    if (error) {
      return { error, file: null }
    }

    const base64Content = isImgMode ? await convertFileToBase64(file) : null
    return {
      error: null,
      file: isImgMode
        ? { name: file.name, content: base64Content }
        : { id: '', name: file.name, content: file, state: 'uploading' }
    }
  }

  //validate multiple files
  const validateFiles = async (
    files: File[],
    isImgMode: boolean,
    fileSizeLimit: number,
    imageSizeLimit: number
  ) => {
    const errorMessages: string[] = []
    const validFiles: FileProps[] = []

    for (const file of files) {
      const { error, file: validatedFile }: any = await validateFile(
        file,
        isImgMode,
        fileSizeLimit,
        imageSizeLimit
      )
      if (error) {
        errorMessages.push(error)
      } else if (validatedFile) {
        validFiles.push(validatedFile)
      }
    }

    return { validFiles, errorMessages }
  }

  const handleImport = async (e: any, dragAndDropFiles?: any[]) => {
    const files = Array.from(dragAndDropFiles ?? (documentFileRef.current?.files || []))

    // Helper to detect image files by MIME type or extension
    const detectIsImageFile = (file: any) =>
      (file.type && file.type.startsWith('image/')) ||
      validImageInput.includes(`.${file.name.split('.').pop()?.toLowerCase()}`)

    const isImgMode = dragAndDropFiles
      ? files.length > 0 && files.every((file: any) => detectIsImageFile(file))
      : documentFileRef.current.accept === validImageInput.toString()

    const fileListArray = files.map(
      (file: any) => new File([file], `${new Date().getTime()}_${file.name}`, { type: file.type })
    )

    if (attachments.count > 0 && !hasImages(files)) {
      toast.error(imgChatError)
      return
    }
    if (isImgMode && imgList.length + fileListArray.length > imageCountLimit) {
      toast.error(imgLimitError)
      return
    }

    if ((fileList.length > 0 && isImgMode) || (imgList.length > 0 && !isImgMode)) {
      toast.error(uploadErrorMessage)
      return
    }

    if (fileList.length + fileListArray.length > fileCountLimit) {
      toast.error(`You can upload a maximum of ${fileCountLimit} files.`)
      return
    }

    const { validFiles, errorMessages }: any = await validateFiles(
      fileListArray,
      isImgMode,
      fileSizeLimit,
      imageSizeLimit
    )
    errorMessages.forEach((error: string) => toast.error(error))

    if (validFiles.length > 0) {
      if (isImgMode) {
        setImgList((prev: any) => [...prev, ...validFiles])
      } else {
        setFileList((prev: any) => [...prev, ...validFiles])
      }
    }

    if (documentFileRef.current?.value) {
      documentFileRef.current.value = ''
    }

    setTimeout(() => {
      inputFileRef.current?.focus()
    }, 5)
  }

  //handle image paste
  const handlePaste = async (event: any) => {
    const items: any = event.clipboardData?.items
    if (!items) return

    const imageFiles = Array.from(items).filter((item: any) => item.type.startsWith('image/'))

    if (imageFiles.length > 0) {
      event.preventDefault()

      if (attachments.count === maxImageLimit) {
        toast.error(maxImgError)
        return
      } else if (imageFiles.length + imgList.length > imageCountLimit) {
        toast.error(imgLimitError)
        return
      }

      const files = imageFiles.map((item: any) => item.getAsFile())
      const { validFiles, errorMessages } = await validateFiles(
        files,
        true,
        fileSizeLimit,
        imageSizeLimit
      )
      errorMessages.forEach((error) => toast.error(error))

      if (validFiles.length > 0) {
        setImgList((prev: any) => [...prev, ...validFiles])
      }
    }
  }

  const removeFile = (fileName: string, type?: string) => {
    if (type && type === 'image') {
      setImgList(imgList.filter((file: any) => file.name !== fileName))
    } else {
      setFileList(fileList.filter((file: any) => file.name !== fileName))
    }
  }

  const dropHandler = (ev: any) => {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault()
    setIsDraggingOver(false)
    if (chatMode === 'smartData') return
    if (attachments.count === maxImageLimit) {
      toast.error(maxImgError)
      return
    }
    handleImport(ev, ev.dataTransfer.files)
  }

  const dragOverHandler = (ev: any) => {
    // Prevent default behavior (Prevent file from being opened)
    ev.preventDefault()
    if (chatMode === 'smartData') return
    setIsDraggingOver(true)
  }

  const onStartListening = async () => {
    const hasSpeechAccess = isMicrophoneAvailable || (await checkSpeechAccess())
    const browserError =
      'Your browser does not support speech recognition. Please try on a different browser.'

    if (!browserSupportsSpeech || !hasSpeechAccess) {
      trackMicrophoneUsed({
        error: !browserSupportsSpeech
          ? 'Speech not supported.'
          : 'Microphone is not available or permission denied.'
      })
      toast.error(
        !browserSupportsSpeech ? browserError : 'Microphone is not available or permission denied.'
      )
      return
    }
    startListening({
      continuous: true,
      onError: (event: any) => {
        console.error('Speech recognition error:', event)
        trackMicrophoneUsed({
          error: 'Speech recognition error.'
        })
        toast.error(browserError)
      }
    })
  }

  const onStopListening = () => {
    stopListening()
    resetTranscript()
  }

  const onAttaching = (type: string) => {
    if (type === 'web-search') {
      if (isAgentWebSearchLocked) {
        setTimeout(() => {
          inputFileRef.current?.focus()
        }, 0)
        return
      }

      setIsWebSearchEnabled(!isWebSearchEnabled)
      setTimeout(() => {
        inputFileRef.current?.focus()
      }, 0)
      return
    }

    if (type === 'add-sources') {
      dataSourceRef.current?.openSourcesModal()
      return
    }

    if (attachments.count === maxImageLimit) {
      toast.error(maxImgError)
      return
    }
    if (attachments.count > 0 && type === 'files') {
      toast.error(imgChatError)
      return
    }
    if (documentFileRef.current) {
      documentFileRef.current.accept =
        type === 'files' ? validFileInput.toString() : validImageInput.toString()
      // Trigger the file input click directly from a user action
      setTimeout(() => {
        documentFileRef.current?.click()
      }, 0)
    }
  }

  // code supporting tool configuration for web search
  // const removeWebSearch = () => {
  //   setIsWebSearchEnabled(false)
  //   setTimeout(() => {
  //     inputFileRef.current?.focus()
  //   }, 0)
  // }

  const setChatConfigTooltip = (type: string) => {
    switch (type) {
      case 'web-search':
        return effectiveWebSearchEnabled
          ? `Web search is ON. ${
              isAgentWebSearchLocked ? 'This is set at agent level and cannot be modified here' : ''
            }`
          : `Web search is OFF`
      case 'files':
        return `Add files (${fileCountLimit} max, ${fileSizeLimit}MB each) only ${[
          ...validFileInput
        ].join(',')}`
      case 'images':
        return `Add image (only 1, upto 1MB) ${[...validImageInput].join(',')}`
      default:
        return 'Add databox(es), data product(s), or other available sources'
    }
  }

  useLayoutEffect(() => {
    setFileListHeight(fileListRef.current?.clientHeight)
  }, [fileList, imgList, isDraggingOver])

  return (
    <div
      className={`flex items-center flex-col gap-1 flex-1 min-h-0 ${
        isDrawerMode && !isFullScreen ? 'w-full' : 'w-[90%] max-w-[800px]'
      }`}
      onDragLeave={onDragLeave}
      onDrop={dropHandler}
      onDragOver={dragOverHandler}>
      <input
        type="file"
        ref={documentFileRef}
        className="d-none"
        accept={[...validFileInput, ...validImageInput].toString()}
        multiple={true}
        onChange={(e: any) => handleImport(e)}
      />
      {isDraggingOver ? (
        <DragDropArea
          isDragging={isDraggingOver}
          targetHeight={fileListHeight}
          message="Drop files here to add them to the conversation"
        />
      ) : (
        <>
          <div
            className={`relative w-full h-full flex flex-col items-center theme-input-surface ${
              hasContextBar ? 'rounded-b-[1rem] rounded-t-none' : 'rounded-[1rem]'
            } border theme-frame-divider chat-primary ${
              isStreaming ? 'chat-input-streaming-border' : ''
            } ${isAgentDeleted ? 'cursor-not-allowed' : ''} h-full`}>
            {(isExpanded || hasOverflow) && (
              <div
                role="button"
                aria-label={isExpanded ? 'Collapse input' : 'Expand input'}
                data-testid="chat-input-expand-toggle"
                onClick={() => setIsExpanded((prev) => !prev)}
                className="absolute top-2 right-10 z-[2] flex items-center justify-center cursor-pointer text-[var(--icon-muted)] opacity-60 hover:opacity-100 transition-opacity duration-200">
                {isExpanded ? (
                  <IconCircleChevronsDown size={24} />
                ) : (
                  <IconCircleChevronsUp size={24} />
                )}
              </div>
            )}
            {isStreaming && (
              <svg
                className="pointer-events-none absolute inset-0 h-full w-full"
                style={{ zIndex: 1 }}
                aria-hidden="true">
                <rect
                  x="0"
                  y="0"
                  width="100%"
                  height="100%"
                  rx={hasContextBar ? 0 : 15}
                  ry={hasContextBar ? 0 : 15}
                  fill="none"
                  stroke="var(--color-primary)"
                  strokeWidth="2"
                  strokeDasharray="25 25"
                  pathLength="100"
                  className="chat-input-border-dash"
                />
              </svg>
            )}
            {/* Deleted Agent Notification Bar */}
            {isAgentDeleted && !isLoadingAgents && (
              <div className="w-full py-1 px-2 rounded-t-[1rem] flex items-center text-sm opacity-100 text-[var(--text-heading)] bg-[var(--chat-theme-secondary-light)]">
                <span className="text-[14px]">
                  This agent was deleted. You can no longer send messages or interact.{' '}
                  <span
                    className="underline cursor-pointer hover:opacity-100"
                    onClick={() => onNewConversationStart()}>
                    Start a new chat
                  </span>
                </span>
              </div>
            )}
            <div
              className={`flex w-full overflow-hidden ${
                isAgentDeleted
                  ? 'rounded-b-[1rem] opacity-60 pointer-events-none'
                  : 'rounded-t-[1rem]'
              } flex-col flex-1 min-h-0 mx-0 chat-primary`}>
              <div className="w-full h-full px-1 flex flex-col">
                <div className="w-full h-full flex flex-col border-none rounded-[30px] transition-[height] duration-[0.36s] ease-in-out outline-none disabled:cursor-not-allowed">
                  <div
                    className={`flex flex-col border-none h-full ${isExpanded ? 'pt-2' : ''}`}
                    ref={fileListRef}>
                    {/* Attached files section - shown above textarea */}
                    <div
                      className={`${
                        (fileList?.length > 0 || imgList?.length > 0) && !isDraggingOver
                          ? 'flex'
                          : 'hidden'
                      } flex-wrap gap-1 pt-1`}>
                      {fileList?.map((file: any, index: number) => (
                        <ChatFile
                          key={index}
                          file={file}
                          onRemoveClick={removeFile}
                          hasRemove={true}
                          shouldUpload={true}
                          setFileList={setFileList}
                        />
                      ))}
                      {imgList?.map((file: any, index: number) => (
                        <ChatImage
                          key={index}
                          file={file}
                          onRemoveClick={removeFile}
                          size="70px"
                          hasRemove={true}
                        />
                      ))}
                    </div>
                    <textarea
                      id="chat-input"
                      className={`!outline-none bg-transparent min-h-auto resize-none my-1 pl-1 pr-3 overflow-y-auto placeholder:text-[var(--icon-muted)] focus:shadow-none text-[var(--text-body)] transition-[max-height] duration-300 ease-in-out ${
                        isExpanded ? 'flex-1 max-h-[100vh]' : 'max-h-[12rem]'
                      } ${isAgentDeleted ? 'cursor-not-allowed' : ''}`}
                      autoComplete="off"
                      disabled={isListening || isAgentDeleted}
                      ref={inputFileRef}
                      placeholder={
                        isAgentDeleted ? '' : `Ask ${selectedAgent?.name || 'your question'}`
                      }
                      value={value}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setValue(e.target.value)
                      }
                      onKeyDown={(event: React.KeyboardEvent) => {
                        if (
                          event.key === 'Enter' &&
                          !event.shiftKey &&
                          !isLoadingAgents &&
                          !isResolvingDataSources
                        ) {
                          event.preventDefault()
                          if (showSubmit && !event.shiftKey && !isAgentDeleted) {
                            handleSubmit(value, fileList, imgList)
                          }
                        }
                      }}
                      onPaste={handlePaste}
                      onFocus={() => setInputFocused(true)}
                      onBlur={() => setInputFocused(false)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`d-flex w-100 justify-content-between items-end mb-25 ${
                isAgentDeleted ? 'pointer-events-none' : ''
              }`}>
              <div className="flex items-center">
                <UncontrolledDropdown>
                  <DropdownToggle
                    tag="div"
                    disabled={disableInput || isAgentDeleted || isStreaming}
                    className={`ml-2 rounded-[0.7rem] p-[0.5rem] flex justify-center items-center self-center hover:bg-[var(--item-hover-bg)] transition-colors duration-200 text-[var(--icon-muted)] ${
                      disableInput || isAgentDeleted || isStreaming
                        ? 'opacity-40 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}
                    id="chat-attach-file">
                    <IconSquarePlus size={20} />
                  </DropdownToggle>

                  <DropdownMenu className="!z-[15]" container="body" strategy="fixed">
                    {attachOptions
                      .filter((option: any) => {
                        // Filter out the "Add sources" option if centrali3_0 flag is false, or
                        // when chatting inside a project.
                        if ((!centrali3_0 || isInProjectChat) && option.id === 'add-sources') {
                          return false
                        }
                        return true
                      })
                      .map((option: any) => {
                        const isWebSearchOption = option.id === 'web-search'
                        const isDisabledWebSearchOption =
                          isWebSearchOption && isAgentWebSearchLocked
                        const optionTextClass =
                          isWebSearchOption && effectiveWebSearchEnabled
                            ? 'text-primary'
                            : 'text-[var(--icon-muted)]'

                        return (
                          <React.Fragment key={option.id}>
                            <DropdownItem
                              id={`menu-${option.id}`}
                              aria-disabled={isDisabledWebSearchOption}
                              className={`px-[12px] ${
                                isDisabledWebSearchOption
                                  ? 'opacity-60 !cursor-not-allowed active:bg-[var(--item-hover-bg)]'
                                  : 'hover:bg-[var(--item-hover-bg)] active:bg-[var(--item-hover-bg)] [&:hover]:text-inherit'
                              }`}
                              onClick={() => {
                                if (isDisabledWebSearchOption) {
                                  return
                                }
                                onAttaching(option.id)
                                document.body.click()
                              }}>
                              <div className="dropdown-option flex gap-50 items-center">
                                {option.icon && (
                                  <span className={`${optionTextClass} flex items-center`}>
                                    {option.icon()}
                                  </span>
                                )}
                                {option?.name && isWebSearchOption ? (
                                  <>
                                    <span className={optionTextClass}>{option.name}</span>
                                    <input
                                      type="checkbox"
                                      className="switch switch--small"
                                      checked={effectiveWebSearchEnabled || false}
                                      disabled={isDisabledWebSearchOption}
                                      readOnly
                                      aria-label={`${option.name} enabled`}
                                    />
                                  </>
                                ) : (
                                  <span className="text-[var(--icon-muted)]">
                                    {option?.name || ''}
                                  </span>
                                )}
                              </div>
                            </DropdownItem>
                            <UncontrolledTooltip placement="left" target={`menu-${option.id}`}>
                              {setChatConfigTooltip(option.id)}
                            </UncontrolledTooltip>
                          </React.Fragment>
                        )
                      })}
                  </DropdownMenu>
                </UncontrolledDropdown>
                {/* code supporting tool config for web search */}
                {/* {isWebSearchEnabled && !isAgentDeleted && (
                  <div
                    className="ml-1 px-[10px] py-[6px] rounded-full"
                    style={{
                      backgroundColor: isSearchHovered
                        ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)'
                        : 'transparent'
                    }}
                    data-testid="web-search-chip">
                    <div
                      className="flex items-center caption-text gap-[5px] leading-none !text-theme-brand-accent"
                      onMouseEnter={() => setIsSearchHovered(true)}
                      onMouseLeave={() => setIsSearchHovered(false)}>
                      <div
                        className="relative rounded-full flex items-center justify-center w-[18px] h-[18px]"
                        style={{
                          backgroundColor: isSearchHovered
                            ? 'color-mix(in srgb, var(--color-primary) 25%, transparent)'
                            : 'transparent'
                        }}>
                        {!isSearchHovered ? (
                          <IconWorld size={16} className="block" stroke="currentColor" />
                        ) : (
                          <IconX
                            size={16}
                            className="block cursor-pointer"
                            stroke="currentColor"
                            onClick={removeWebSearch}
                          />
                        )}
                      </div>
                      <span className="text-button-small leading-none">Search</span>
                    </div>
                  </div>
                )} */}
                {!isAgentDeleted && !isInProjectChat && (
                  <DataSourceSelect
                    key={selectedAgent?.id || 'no-agent'}
                    ref={dataSourceRef}
                    isMarketplace={isMarketplace}
                    isDisabled={isStreaming}
                    dataProductsInChat={dataProductsInChat}
                    agentSources={{
                      databoxIds: agentDataboxIds,
                      dataProductIds: agentDataProductIds,
                      agentId: selectedAgent?.id,
                      teamIdentifiers: agentTeamIdentifiers,
                      cognitiveSearchIds: agentCognitiveSearchIds,
                      dataProductsByDatabox: agentDataProductsByDatabox,
                      smartInsightsTeamIdentifier: agentSmartInsightsTeamIdentifier,
                      isTeamAgent: selectedAgent?.agentType === 'Team',
                      isEnterpriseAgent: selectedAgent?.agentType === 'Enterprise',
                      agentScopeId: selectedAgent?.scopeId,
                      personalDataboxResourceIds: agentSourcePersonalDataboxResourceIds,
                      personalDataBoxId: agentSourcePersonalDataBoxId
                    }}
                    threadSources={{
                      databoxIds: threadSourceDataboxIds,
                      dataProductIds: threadSourceDataProductIds,
                      cognitiveSearchIds: threadSourceCognitiveSearchIds,
                      dataProductsByDatabox: threadSourceDataProductsByDatabox,
                      personalDataboxResourceIds: threadSourcePersonalDataboxResourceIds,
                      personalDataBoxId: threadSourcePersonalDataBoxId
                    }}
                    isExistingChat={currentChat?.id !== undefined && currentChat?.id !== null}
                    hasMessages={messages.length > 0}
                    chatId={currentChat?.id ?? null}
                    isFetchingThreadData={fetchingMsgs}
                    onDataSourcesChange={setDataSourceState}
                    isResolvingDataSources={isResolvingDataSources}
                    setIsResolvingDataSources={setIsResolvingDataSources}
                  />
                )}
              </div>
              <div className="d-flex align-self-end mx-50 chat-tools">
                {!isListening && (
                  <div className="d-flex items-end">
                    {/* {isMarketplace && (
                      <Select
                        ref={selectRef}
                        name="products"
                        onChange={(option: any, actionMeta: any) => {
                          dispatch(setProducts(option))
                        }}
                        options={datasets || []}
                        value={products || []}
                        components={{
                          Option: CustomOption,
                          ValueContainer: MultiValueBadge,
                          DropdownIndicator: CustomDropdownIndicator
                        }}
                        placeholder="Select data product/s"
                        menuPlacement="auto"
                        multiValueLabel={(count: number) => (count === 1 ? 'product' : 'products')}
                        closeMenuOnSelect={false}
                        className="min-w-[200px] ml-[20px] react-select"
                        disabled={isFetchingDatasets}
                        isMulti
                        isClearable
                        classNamePrefix="select"
                        styles={{
                          control: (base: any) => ({
                            ...base,
                            borderColor: 'var(--theme-border)',
                            backgroundColor: 'var(--color-primary-10)'
                          }),
                          placeholder: (base: any) => ({
                            ...base,
                            color: 'var(--color-primary)'
                          }),
                          option: (style: any) => ({ ...style, color: 'black', margin: '3px 0' }),
                          menuPortal: (base: any) => ({ ...base, zIndex: 999 })
                        }}
                        showSelectedAsChecked={true}
                        hideSelectedOptions={false}
                        ValueContainer={MultiValueBadge}
                        theme={selectThemeColors}
                        menuIsOpen={menuIsOpen}
                        onMenuOpen={() => setMenuIsOpen(true)}
                        onMenuClose={() => setMenuIsOpen(false)}
                        onBlur={() => setMenuIsOpen(false)}
                      />
                    )} */}
                    {chatModels && chatModels.length > 0 && !isAgentDeleted && (
                      <ChatModelDropdown />
                    )}
                  </div>
                )}

                {isLoadingAgents || isResolvingDataSources ? (
                  <Spinner color="primary" className="ml-50" />
                ) : showSubmit && !isListening && !isLoadingAgents ? (
                  <>
                    <div
                      data-tooltip-id="chat-send"
                      onClick={() => !isAgentDeleted && handleSubmit(value, fileList, imgList)}
                      className={`ml-3 rounded-[0.7rem] p-[0.5rem] flex justify-center items-center self-center ${
                        isAgentDeleted
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-[var(--color-primary)] cursor-pointer'
                      }`}>
                      <IconSend className="z-100" size={18} data-testid="send" color={'white'} />
                    </div>
                    {/* <Send
                id="chat-send"
                data-testid="send"
                color={
                  disableInput || (value.trim().length === 0 && imgList?.length === 0)
                    ? 'gray'
                    : 'var(--color-primary)'
                }
                size={16}
                className={`ml-3 self-center ${
                  disableInput || (value.trim().length === 0 && imgList?.length === 0)
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer send-button'
                }`}
                onClick={() => handleSubmit(value, fileList, imgList)}
              />*/}
                  </>
                ) : isStreaming ? (
                  <>
                    <div
                      data-tooltip-id="stop-responding"
                      data-testid="stop"
                      onClick={() => cancelAPICall()}
                      className={`ml-3 rounded-[0.7rem] p-[0.5rem] flex justify-center items-center self-center bg-[var(--color-primary)] cursor-pointer`}>
                      <IconSquare size={14} color={'white'} fill={'white'} />
                    </div>
                    <Tooltip
                      id="stop-responding"
                      place="bottom"
                      className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                      Stop Responding
                    </Tooltip>
                  </>
                ) : (
                  <div
                    className={`flex justify-between items-center rounded-[80px] transition-all duration-300 ease-out ${
                      isListening ? 'theme-bg w-[300px] pl-2' : 'bg-transparent pl-0'
                    }`}>
                    {isListening && (
                      <WaveSound size={25} color={'var(--white)'} shouldWave={showWaveSound} />
                    )}
                    {isListening && (
                      <div className="text-[var(--color-secondary)] pl-[15px]">
                        {formattedMinutes}:{formattedSeconds}
                      </div>
                    )}
                    {isListening ? (
                      <>
                        <div
                          data-tooltip-id="stop-recording"
                          onClick={onStopListening}
                          className={`ml-[5px] rounded-full w-10 h-10 p-[10px] flex justify-center items-center self-center chat-primary theme-bg cursor-pointer`}
                          role="button">
                          <StopCircle size={28} color={'var(--color-secondary)'} />
                        </div>
                        <Tooltip
                          id="stop-recording"
                          place="bottom"
                          className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                          Stop Listening
                        </Tooltip>
                      </>
                    ) : isLoadingAgents ? (
                      <Spinner color="primary" className="ml-50" />
                    ) : (
                      <>
                        <div
                          data-tooltip-id="mic-recording"
                          onClick={
                            hasMicrophoneAccess && !isAgentDeleted ? onStartListening : undefined
                          }
                          className={`ml-3 rounded-[0.7rem] p-[0.5rem] flex justify-center items-center self-center transition-colors duration-200 ${
                            hasMicrophoneAccess && !isAgentDeleted
                              ? 'cursor-pointer hover:bg-[var(--item-hover-bg)]'
                              : 'cursor-not-allowed opacity-50'
                          }`}>
                          {hasMicrophoneAccess && !isAgentDeleted ? (
                            <IconMicrophone
                              className="outline-none"
                              size={20}
                              data-tooltip-id="mic-recording"
                            />
                          ) : (
                            <IconMicrophoneOff
                              className="outline-none"
                              size={20}
                              data-tooltip-id="mic-recording-off"
                            />
                          )}
                        </div>
                        <Tooltip
                          id="mic-recording"
                          place="bottom"
                          className="!rounded-md p-[10px] max-w-[250px] text-center z-[9999]">
                          {isAgentDeleted
                            ? 'Agent deleted'
                            : hasMicrophoneAccess
                            ? 'Start Listening'
                            : 'Microphone not available'}
                        </Tooltip>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {messages.length === 0 && !isExpanded && (
        <>
          <div
            className="flex self-end cursor-pointer gap-25 mr-2 text-theme-subheading text-body-default"
            onClick={() => setOpenPromptLib(true)}>
            <PromptLibraryIcon color="var(--text-subheading)" size={18} />
            Prompt library
          </div>
          {openPromptLib && (
            <PromptLibraryModal
              open={openPromptLib}
              setOpen={setOpenPromptLib}
              suggestions={shuffledPromptSuggestions}
              setPromptText={setPromptText}
            />
          )}
        </>
      )}
    </div>
  )
}
